/**
 * Who is signed in, and the way out.
 *
 * "Also how do we log out" — there was no answer to that. `signOut` existed in
 * the auth client and nothing in the app ever called it, so the only way off a
 * shared machine was to close every tab and hope the session expired.
 *
 * It lives in the top bar because that is the one piece of chrome on every
 * screen at every width (the side nav hides below lg), and because the avatar
 * in the top right corner is the one place everybody already looks for this.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { LogOut, Settings as SettingsIcon, KeyRound, Loader2 } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { clearBearerToken } from "@/lib/auth-store";
import { signOut, authClient } from "@/lib/auth-client";
import { canAny, useMe } from "@/lib/me";
import { cn } from "@/lib/utils";
import { Modal, ModalFooter, Button, Input, Label, useToast } from "@/components/ui";

/**
 * End the session and come back to the login screen.
 *
 * Order matters: the server session dies first, then the in-memory bearer, and
 * only then the reload. A reload with the cookie still alive would sign them
 * straight back in, which is exactly the bug this is here to prevent on a
 * front-desk computer. `replace` rather than `assign` so Back cannot return to
 * a logged-in screen.
 */
export async function endSession(): Promise<void> {
  try {
    await signOut();
  } catch {
    // Already gone, or the network is down. Clearing locally and reloading
    // still gets them off this screen, which is the point.
  }
  clearBearerToken();
  try { sessionStorage.clear(); } catch { /* storage unavailable */ }
  window.location.replace("/");
}

export function AccountMenu() {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Click-outside + Escape close the popover, same as the bell beside it.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!me) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-account-menu
        onClick={() => setOpen((o) => !o)}
        aria-label={`Signed in as ${me.name || me.email}`}
        aria-expanded={open}
        className={cn(
          "flex items-center rounded-full p-0.5 transition-colors",
          "hover:bg-zinc-800/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400",
          open && "bg-zinc-800/60",
        )}
      >
        <Avatar name={me.name} email={me.email} size="sm" />
      </button>

      {changing && <ChangePassword onClose={() => setChanging(false)} />}

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-64 max-w-[92vw] animate-fade-in rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-black/40">
          <div className="flex items-center gap-2.5 border-b border-zinc-800 px-3 py-2.5">
            <Avatar name={me.name} email={me.email} size="sm" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-zinc-100">
                {me.name || "Signed in"}
                {me.credential && <span className="ml-1 font-normal text-zinc-500">{me.credential}</span>}
              </p>
              <p className="truncate text-[11px] text-zinc-500">{me.email}</p>
            </div>
          </div>

          <div className="p-1">
            {canAny(me, ["settings", "staff"]) && (
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100"
              >
                <SettingsIcon className="h-4 w-4" />
                Settings
              </Link>
            )}
            {/* "did we set up the password change so we can create our own"
                — asked twice. Everybody has one, whatever their role, so it
                lives next to the way out rather than in Settings, which most
                of her staff cannot open. */}
            <button
              type="button"
              data-change-password
              onClick={() => { setOpen(false); setChanging(true); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100"
            >
              <KeyRound className="h-4 w-4" />
              Change my password
            </button>
            <button
              type="button"
              data-sign-out
              disabled={leaving}
              onClick={() => { setLeaving(true); void endSession(); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-zinc-300 hover:bg-rose-500/10 hover:text-rose-300 disabled:opacity-60"
            >
              <LogOut className="h-4 w-4" />
              {leaving ? "Signing out…" : "Sign out"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Change your own password, without anybody resetting it for you.
 *
 * The current one is asked for because this is the screen somebody walks away
 * from: a signed-in machine left open should not be enough to lock its owner
 * out of their own login.
 */
function ChangePassword({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await authClient.changePassword({
        currentPassword: current,
        newPassword: next,
        revokeOtherSessions: true,
      });
      const err = (res as { error?: { message?: string } }).error;
      if (err) {
        setError(/password|credential/i.test(err.message ?? "")
          ? "That current password isn't right."
          : err.message || "Couldn't change it.");
        return;
      }
      toast.success("Password changed");
      onClose();
    } catch {
      setError("Couldn't reach the server. Try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Change my password" size="sm">
      <form onSubmit={save} className="space-y-3">
        <div>
          <Label required>Current password</Label>
          <Input type="password" autoComplete="current-password" autoFocus
            value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div>
          <Label required>New password</Label>
          <Input type="password" autoComplete="new-password" minLength={8}
            value={next} onChange={(e) => setNext(e.target.value)} />
          <p className="mt-1 text-[11px] text-zinc-500">At least 8 characters.</p>
        </div>
        {error && (
          <p role="alert" className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs text-rose-300">{error}</p>
        )}
        <p className="text-[11px] text-zinc-500">
          Changing it signs you out everywhere else, so a machine you left open stops being signed in.
        </p>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          <Button type="submit" loading={busy} disabled={!current || next.length < 8}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Change it
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
